import { forwardRef, type PropsWithChildren } from 'react';
import styled from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { SnapCarousel } from '../../main';
import { useLayout } from '../layout-context';
import { SnapCarouselWrapper } from './snap-carousel-wrapper';
import { tableCarouselItemMixin } from './table-carousel-item-mixin';
import { tableMixin } from './table-mixin';

const COLUMNS_COUNT = 3;

export const ComparisonTableCarousel = forwardRef<
  HTMLDivElement,
  PropsWithChildren
>(({ children }, ref) => {
  const { carouselArrowSize } = useSettings();
  const { rowsCount } = useLayout();

  return (
    <SnapCarouselWrapper ref={ref} component="layout-comparison-table">
      <Container>
        <OuterTableContainer>{children}</OuterTableContainer>

        <InnerTableContainer>
          <StyledSnapCarousel
            arrowControlMarginLeft={`calc(100% / ${COLUMNS_COUNT} - ${carouselArrowSize}px / 2)`}
            $rowsCount={rowsCount}
          >
            {children}
          </StyledSnapCarousel>
        </InnerTableContainer>
      </Container>
    </SnapCarouselWrapper>
  );
});

const OuterTableContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'layout-comparison-table',
    element: 'outer-table-container'
  })
})`
  display: grid;
  grid-auto-columns: calc(100% / ${COLUMNS_COUNT});
  grid-area: 1 / 1 / 2 / span ${COLUMNS_COUNT};

  & > div:first-child::after {
    z-index: 2;
    box-shadow:
      0 0 20px 0 rgba(17, 17, 17, 0.1),
      0 0 50px 0 rgba(17, 17, 17, 0);
  }

  // Скрываем все колонки кроме head
  & > div:not(:first-child) {
    & * {
      opacity: 0;
    }

    &::after,
    &::before {
      content: none;
    }
  }
`;

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'layout-comparison-table',
    element: 'container'
  })
})`
  display: grid;
  grid-auto-columns: calc(100% / ${COLUMNS_COUNT});
  ${tableMixin}
`;

const InnerTableContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'layout-comparison-table',
    element: 'inner-table-container'
  })
})`
  position: relative;
  grid-area: 1 / 2 / 2 / span ${COLUMNS_COUNT - 1};

  & > div {
    position: static;
  }
`;

const StyledSnapCarousel = styled(SnapCarousel).attrs<{
  $rowsCount: number;
}>({
  columnsGap: 0,
  className: createReadableClass({
    component: 'layout-comparison-table',
    element: 'snap-carousel'
  })
})`
  display: grid;
  margin: 0 auto;
  grid-auto-columns: calc(100% / ${COLUMNS_COUNT - 1});
  ${tableCarouselItemMixin};

  & > div:nth-child(2) {
    grid-column: 1;
  }

  // Делаем head колонку невидимой в самой карусели
  & > div:first-child {
    & {
      display: contents;
    }

    & * {
      opacity: 0;
    }

    ::after,
    ::before {
      content: none;
    }
  }
`;
